import React, { FC, useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Collapse,
    FormControlLabel,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingResource, pricingResourceItem } from "../../../api/endpoints";
import { ContentType, ContentTypePriceRow, usePricing } from "./PricingContext";
import { SnapshotBanner } from "./SnapshotBanner";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { titleizeName } from "../../../utils";
import { LineItemsEditor } from "./LineItemsEditor";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import ContentTypeModal from "./components/ContentTypeModal";

type PriceField = "base_price" | "per_product_base_price";

export const ContentTypesTab: FC = () => {
    const { data, contentTypes, loading, refetch, canEdit } = usePricing();
    const [busyId, setBusyId] = useState<number | null>(null);
    // Types without "Portal Enabled" (expert_connect) are treated as archived.
    const [showArchived, setShowArchived] = useState(false);
    const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingType, setEditingType] = useState<ContentType | null>(null);
    const [typeToDelete, setTypeToDelete] = useState<ContentType | null>(null);
    // Content type ids whose cost sheet is expanded.
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const toggleExpanded = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        axiosInstant
            .get("/brands?limit=100")
            .then((res) => setBrands(res.data?.rows || []))
            .catch(() => toast.error("Failed to fetch brands."));
    }, []);

    if (loading || !data) return <DotLottleLoader />;

    const priceByTypeId = new Map<number, ContentTypePriceRow>(
        data.content_type_prices.map((row) => [row.content_type_id, row])
    );

    const archivedCount = contentTypes.filter((ct) => !ct.expert_connect).length;
    const visibleTypes = showArchived
        ? contentTypes
        : contentTypes.filter((ct) => ct.expert_connect);

    const handlePriceCommit = async (contentType: ContentType, field: PriceField, raw: string) => {
        if (raw === "") return;
        const v = Number(raw);
        if (!Number.isFinite(v)) return;
        const existing = priceByTypeId.get(contentType.id);
        if (existing && v === Number(existing[field])) return;
        setBusyId(contentType.id);
        try {
            if (existing) {
                await axiosInstant.patch(pricingResourceItem("content-type-prices", existing.id), {
                    [field]: v
                });
            } else {
                await axiosInstant.post(pricingResource("content-type-prices"), {
                    content_type_id: contentType.id,
                    [field]: v
                });
            }
            toast.success("Saved");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyId(null);
        }
    };

    const handleSaveType = async (payload: any) => {
        try {
            if (editingType?.id) {
                await axiosInstant.patch(`/content-types/${editingType.id}`, payload);
                toast.success("Content Type updated.");
            } else {
                await axiosInstant.post("/content-types", payload);
                toast.success("Content Type created.");
            }
            setShowModal(false);
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error saving content type.");
        }
    };

    const handleDeleteType = async () => {
        if (!typeToDelete) return;
        const id = typeToDelete.id;
        setTypeToDelete(null);
        setBusyId(id);
        try {
            await axiosInstant.delete(`/content-types/${id}`);
            toast.success("Deleted successfully.");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error deleting content type.");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Box>
            <Box className="flex flex-wrap items-center justify-between gap-3" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Content Types &amp; Base Prices
                </Typography>
                <Box className="flex items-center gap-2">
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={showArchived}
                                onChange={(e) => setShowArchived(e.target.checked)}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: 14 }}>
                                Show archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
                            </Typography>
                        }
                    />
                    {canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingType(null);
                                setShowModal(true);
                            }}
                        >
                            Add Content Type
                        </Button>
                    )}
                </Box>
            </Box>
            <SnapshotBanner />
            <Typography color="var(--text-2)" sx={{ fontSize: 13, lineHeight: 1.6, mb: 2 }}>
                Expand a row to edit the cost sheet behind its base price — the line items
                shown to clients as what the price covers.
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }} />
                            <TableCell>
                                <strong>Content Type</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Base Price</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Per-Product Base Price</strong>
                            </TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleTypes.map((ct) => {
                            const price = priceByTypeId.get(ct.id);
                            const busy = busyId === ct.id;
                            const isOpen = expanded.has(ct.id);
                            return (
                                <React.Fragment key={ct.id}>
                                <TableRow sx={ct.expert_connect ? undefined : { opacity: 0.5 }}>
                                    <TableCell sx={{ width: 40 }}>
                                        <IconButton
                                            size="small"
                                            title="Cost sheet"
                                            onClick={() => toggleExpanded(ct.id)}
                                        >
                                            {isOpen ? (
                                                <ExpandMoreIcon fontSize="small" />
                                            ) : (
                                                <ChevronRightIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <Box className="flex items-center gap-2">
                                            <Typography sx={{ fontSize: 14 }}>{titleizeName(ct.name)}</Typography>
                                            {ct.expert_connect ? (
                                                <Chip label="Portal Enabled" size="small" variant="outlined" />
                                            ) : (
                                                <Chip label="Archived" size="small" variant="outlined" color="default" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {price ? (
                                            // Derived: base price is the sum of the cost sheet.
                                            // Move it via the sheet (or its re-foot control).
                                            <Box>
                                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                                    {formatCurrency(price.base_price)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: "var(--text-2)" }}>
                                                    = cost sheet
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <TextField
                                                size="small"
                                                type="number"
                                                key={`bp-${ct.id}-new`}
                                                placeholder="—"
                                                helperText="No price set — enter one to create the sheet"
                                                disabled={busy || !canEdit}
                                                onBlur={(e) =>
                                                    handlePriceCommit(ct, "base_price", e.target.value)
                                                }
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            key={`pp-${ct.id}-${price?.id ?? "new"}`}
                                            defaultValue={price?.per_product_base_price ?? ""}
                                            placeholder="—"
                                            helperText={
                                                price ? formatCurrency(price.per_product_base_price) : "No price set"
                                            }
                                            disabled={busy || !canEdit}
                                            onBlur={(e) =>
                                                handlePriceCommit(ct, "per_product_base_price", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {canEdit && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingType(ct);
                                                        setShowModal(true);
                                                    }}
                                                    disabled={busy}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setTypeToDelete(ct)}
                                                    disabled={busy}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 0, border: isOpen ? undefined : 0 }}>
                                        <Collapse in={isOpen} unmountOnExit>
                                            {price ? (
                                                <LineItemsEditor
                                                    items={price.line_items ?? []}
                                                    contentTypePriceId={price.id}
                                                    canEdit={canEdit}
                                                    onChanged={refetch}
                                                />
                                            ) : (
                                                <Typography
                                                    color="textSecondary"
                                                    sx={{ fontSize: 13, py: 2 }}
                                                >
                                                    Set a base price first — the cost sheet attaches to
                                                    the price row.
                                                </Typography>
                                            )}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <ContentTypeModal
                open={showModal}
                onSave={handleSaveType}
                onClose={() => setShowModal(false)}
                brands={brands}
                initialData={editingType || { name: "", deactivated: false, expert_connect: true }}
            />

            <ConfirmationDialog
                open={!!typeToDelete}
                onClose={() => setTypeToDelete(null)}
                onConfirm={handleDeleteType}
                title="Delete Content Type"
                description="Are you sure you want to delete this content type? Its saved prices are kept but no longer used."
                confirmLabel="Delete"
            />
        </Box>
    );
};

export default ContentTypesTab;
